import React, { useState, useEffect, useCallback } from 'react';
import logger from '../lib/logger';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { challengeAPI } from '../lib/api';
import {
  Clock, AlertCircle, ChevronLeft, ChevronRight, Flag, 
  Loader2, CheckCircle2, Circle, Timer, Calculator
} from 'lucide-react';
import { toast } from 'sonner';
import { renderLatex } from '../lib/renderLatex';
import LoadingSpinner from '../components/LoadingSpinner';

const ChallengeTest = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Fetch challenge data
  useEffect(() => {
    const fetchChallenge = async () => {
      try {
        const response = await challengeAPI.getByCode(code);
        const data = response.data;
        
        
        
        if (!data.has_joined) {
          toast.error('You are not part of this challenge');
          navigate('/challenges');
          return;
        }
        
        setChallenge(data);
        
        // Calculate remaining time
        if (data.started_at) {
          const startTime = new Date(data.started_at).getTime();
          const durationMs = data.duration_minutes * 60 * 1000;
          const endTime = startTime + durationMs;
          const remaining = Math.max(0, Math.floor((endTime - Date.now()) / 1000));
          setTimeLeft(remaining);
        } else {
          setTimeLeft(data.duration_minutes * 60);
        }
      } catch (err) {
        logger.error('Failed to fetch challenge:', err);
        setError(err.response?.data?.detail || 'Failed to load challenge');
      } finally {
        setLoading(false);
      }
    };

    fetchChallenge();
  }, [code, navigate]);

  // Timer countdown
  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0 || submitted) return;
    
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmit(); // Auto-submit when time runs out
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, submitted, code]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAnswer = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleSubmit = useCallback(async () => {
    if (submitting || submitted) return;
    
    setSubmitting(true);
    try {
      const response = await challengeAPI.submit(code, { answers });
      toast.success(`Challenge submitted! Score: ${response.data.score}%`);
      setSubmitted(true);
      
      // Redirect to lobby to see results
      setTimeout(() => {
        navigate(`/challenge/${code}`);
      }, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
      setSubmitting(false);
    }
  }, [code, answers, submitting, submitted, navigate]);

  const goToQuestion = (index) => {
    if (index >= 0 && index < (challenge?.questions_data?.length || 0)) {
      setCurrentIndex(index);
    }
  };

  // Loading state
  if (loading) {
    return <LoadingSpinner text="Loading challenge..." fullScreen={true} size="xl" />;
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="text-red-500 mx-auto mb-4" size={48} />
          <h1 className="text-2xl font-bold text-foreground dark:text-white mb-4">Error</h1>
          <p className="text-muted-foreground mb-8">{error}</p>
          <Button onClick={() => navigate('/challenges')}>Back to Challenges</Button>
        </div>
      </div>
    );
  }

  // Submitted state
  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <CheckCircle2 className="text-green-500 mx-auto mb-4" size={64} />
          <h1 className="text-2xl font-bold text-foreground dark:text-white mb-4">
            Challenge Submitted!
          </h1>
          <p className="text-muted-foreground">Redirecting to results...</p>
        </div>
      </div>
    );
  }

  const questions = challenge?.questions_data || [];
  const currentQuestion = questions[currentIndex];

  if (!currentQuestion) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="text-yellow-500 mx-auto mb-4" size={48} />
          <h1 className="text-2xl font-bold text-foreground dark:text-white mb-4">
            No Questions Available
          </h1>
          <p className="text-muted-foreground mb-8">This challenge has no questions.</p>
          <Button onClick={() => navigate('/challenges')}>Back to Challenges</Button>
        </div>
      </div>
    );
  }

  const answeredCount = Object.keys(answers).length;
  const isLowTime = timeLeft !== null && timeLeft < 60;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className={`sticky top-0 z-50 border-b ${
        isLowTime ? 'bg-red-50 dark:bg-red-950/50 border-red-200 dark:border-red-800' : 'bg-card border-border'
      }`}>
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* ExamDex Branding */}
            <div className="w-14 h-14 rounded-xl flex items-center justify-center shadow-md overflow-hidden">
              <img src="/logo192.png" alt="ExamDex" className="w-full h-full object-cover" />
            </div>
            <div>
              <h1 className="font-bold text-foreground dark:text-white">
                {challenge.title || challenge.exam_name}
              </h1>
              <p className="text-xs text-muted-foreground">
                Question {currentIndex + 1} of {questions.length}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className={`flex items-center gap-2 px-4 py-2 rounded-lg font-mono text-lg font-bold ${
              isLowTime 
                ? 'bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400 animate-pulse' 
                : 'bg-muted text-foreground dark:text-white'
            }`}>
              <Timer size={20} />
              {timeLeft !== null ? formatTime(timeLeft) : '--:--'}
            </div>
            
            <Badge className="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
              {answeredCount}/{questions.length} answered
            </Badge>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main Question Area */}
          <div className="lg:col-span-3">
            <Card className="p-6 bg-card rounded-2xl border border-border">
              {/* Question */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                    Q{currentIndex + 1}
                  </Badge>
                </div>
                <p 
                  className="text-lg text-foreground dark:text-white whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{ __html: renderLatex(currentQuestion.question_text) }}
                />
              </div>

              {/* Options */}
              <div className="space-y-3">
                {/* Check if it's a numerical question (no options or empty options) */}
                {(!currentQuestion.options || 
                  (Array.isArray(currentQuestion.options) && currentQuestion.options.length === 0) ||
                  (typeof currentQuestion.options === 'object' && Object.keys(currentQuestion.options).length === 0)) ? (
                  /* Numerical input */
                  <div className="p-4 bg-muted/30 rounded-xl border border-border">
                    <div className="flex items-center gap-3 mb-3">
                      <Calculator className="text-purple-500" size={20} />
                      <span className="text-sm font-medium text-muted-foreground">Enter your answer</span>
                    </div>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={answers[currentQuestion.id] || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || /^-?\d*\.?\d*$/.test(val)) {
                          handleAnswer(currentQuestion.id, val);
                        }
                      }}
                      placeholder="Type your answer here..."
                      className="w-full px-4 py-3 bg-background border-2 border-border rounded-lg text-lg font-mono text-foreground focus:outline-none focus:border-purple-500 transition-colors"
                    />
                  </div>
                ) : Array.isArray(currentQuestion.options) ? (
                  // Array format: [{label: "A", text: "option text"}, ...]
                  currentQuestion.options.map((option, idx) => {
                    const key = option.label || String.fromCharCode(65 + idx);
                    const value = option.text || option;
                    const isSelected = answers[currentQuestion.id] === key;
                    return (
                      <button
                        key={idx}
                        onClick={() => handleAnswer(currentQuestion.id, key)}
                        className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                          isSelected
                            ? 'border-purple-500 bg-purple-50 dark:bg-purple-950/30'
                            : 'border-border hover:border-purple-300 hover:bg-muted/50'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold ${
                            isSelected 
                              ? 'bg-purple-500 text-white' 
                              : 'bg-muted text-muted-foreground'
                          }`}>
                            {key}
                          </div>
                          <p 
                            className="text-foreground dark:text-white flex-1"
                            dangerouslySetInnerHTML={{ __html: renderLatex(typeof value === 'string' ? value : '') }}
                          />
                        </div>
                      </button>
                    );
                  })
                ) : (
                  // Object format: {A: "option text", B: "option text", ...}
                  Object.entries(currentQuestion.options || {}).map(([key, value]) => {
                    const isSelected = answers[currentQuestion.id] === key;
                    return (
                      <button
                        key={key}
                        onClick={() => handleAnswer(currentQuestion.id, key)}
                        className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                          isSelected
                            ? 'border-purple-500 bg-purple-50 dark:bg-purple-950/30'
                            : 'border-border hover:border-purple-300 hover:bg-muted/50'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold ${
                            isSelected 
                              ? 'bg-purple-500 text-white' 
                              : 'bg-muted text-muted-foreground'
                          }`}>
                            {key}
                          </div>
                          <p 
                            className="text-foreground dark:text-white flex-1"
                            dangerouslySetInnerHTML={{ __html: renderLatex(typeof value === 'string' ? value : '') }}
                          />
                        </div>
                      </button>
                    );
                  })
                )}
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
                <Button
                  onClick={() => goToQuestion(currentIndex - 1)}
                  disabled={currentIndex === 0}
                  variant="outline"
                  className="gap-2"
                >
                  <ChevronLeft size={18} />
                  Previous
                </Button>

                {currentIndex < questions.length - 1 ? (
                  <Button
                    onClick={() => goToQuestion(currentIndex + 1)}
                    className="gap-2"
                  >
                    Next
                    <ChevronRight size={18} />
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="gap-2 bg-gradient-to-r from-green-500 to-emerald-500"
                  >
                    {submitting ? <Loader2 className="animate-spin" size={18} /> : <Flag size={18} />}
                    Submit Challenge
                  </Button>
                )}
              </div>
            </Card>
          </div>

          {/* Question Navigator */}
          <div className="lg:col-span-1">
            <Card className="p-4 bg-card rounded-2xl border border-border sticky top-24">
              <h3 className="font-semibold text-foreground dark:text-white mb-4">
                Questions
              </h3>
              <div className="grid grid-cols-5 gap-2">
                {questions.map((q, idx) => {
                  const isAnswered = answers[q.id] !== undefined;
                  const isCurrent = idx === currentIndex;
                  return (
                    <button
                      key={q.id}
                      onClick={() => goToQuestion(idx)}
                      className={`w-10 h-10 rounded-lg font-semibold text-sm transition-all ${
                        isCurrent
                          ? 'bg-purple-500 text-white'
                          : isAnswered
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-300 dark:border-green-700'
                          : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                    >
                      {idx + 1}
                    </button>
                  );
                })}
              </div>

              <div className="mt-6 pt-4 border-t border-border">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                  <div className="w-4 h-4 rounded bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700"></div>
                  <span>Answered ({answeredCount})</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <div className="w-4 h-4 rounded bg-muted"></div>
                  <span>Unanswered ({questions.length - answeredCount})</span>
                </div>
              </div>

              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full mt-6 gap-2 bg-gradient-to-r from-green-500 to-emerald-500"
              >
                {submitting ? <Loader2 className="animate-spin" size={18} /> : <Flag size={18} />}
                Submit
              </Button>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChallengeTest;
